import { DataTableProvider } from 'components/DataTable'

import { ContactResponse } from 'models/contacts'

import {
  getUniversitiesSummaryForTable,
  listUniversitiesForTable,
} from 'services/universities'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { FilterIdentifier } from '../../../../models/saved_view'
import { IColumnVisibility } from '../../../DataTable/types'
import { UniversitiesFilterset } from './UniversitiesFilterset'
import { useUniversitiesTableCols } from './universitiesTableColumns'

import { DECommonTable } from 'components/DataExpansionComponents/Common/CommonTable/CommonTable'
import UniversityExportModal from 'components/Modals/ExportModal/UniversityExportModal'
import { dataTableSortingStoreRepo } from '../../../DataTable/DataTableSorting/DataTableSortingStore'
import { StandardSummaryResponse } from 'models/paginated-response'
import { useUnifiedTableActions } from 'services/opportunities/useUnifiedTableActions'
type SortableFields = (keyof ContactResponse | string)[]

// University detail link reads `university.institution_id` (see
// universitiesTableColumns name cell), not `.id`.
const UNIFIED_ROW_CONFIG = {
  nestKey: 'university',
  detailLinkIdField: 'institution_id',
}

const sortableFields: SortableFields = [
  'institution_name',
  'total_students_enrolled',
  'note_count',
  'contact_count',
  'deal_count',
  'address',
  'city',
  'state_abbr',
  'zipcode',
  'domain',
  'dormitory_capacity',
  'meal_plan_provided',
  'total_enrollment',
  'food_management_company',
  'food_management_company_parent',
  'estimated_meals_per_day',
  'carnegie_classification_2021_size_setting_size',
  'carnegie_classification_2021_size_setting_year',
  'carnegie_classification_2021_size_setting_student_composition',
  'sales_stages',
]

interface UniversitiesTableProps {
  setTotalRowsCount?: (count: StandardSummaryResponse) => void
  pageParamName?: string
  baseFilters?: Record<string, any>
  tableKey: string
  filterIdentifierModifier?: FilterIdentifier
  hideCreateCompanyButton?: boolean
}

function UniversitiesTableComponent(props: UniversitiesTableProps) {
  const { tableKey, ...rest } = props
  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()
  const columns = useUniversitiesTableCols({
    sorting,
    inCampaigns: props.baseFilters?.main_campaign,
  })
  const { listAction, summaryAction } = useUnifiedTableActions(
    'university',
    UNIFIED_ROW_CONFIG,
    {
      listAction: listUniversitiesForTable,
      summaryAction: getUniversitiesSummaryForTable,
    }
  )

  return (
    <DECommonTable
      {...rest}
      tableKey={tableKey}
      columns={columns}
      sortableFields={sortableFields}
      filterIdentifier="CNU_TABLE"
      apiGetListAction={listAction}
      apiGetSummaryAction={summaryAction}
      companyTypeSlug="education-cu"
      tableTypeSlug="education-cu"
      searchPlaceholder="Search universities by name"
      FilterSet={({ filterIdentifier, hideCampaignsFilter }) => (
        <UniversitiesFilterset
          filterIdentifier={filterIdentifier}
          hideCampaignsFilter={hideCampaignsFilter}
        />
      )}
      ExportModal={UniversityExportModal}
    />
  )
}

export function UniversitiesTable(
  props: UniversitiesTableProps & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const { tableKey, defaultColumnVisibility, ...rest } = props

  return (
    <ColumnsStoreProvider
      savedPresetsIdentifier="CNU_TABLE"
      tableKey={tableKey}
      defaultColumnVisibility={defaultColumnVisibility}
      preferredGroupOrder={[
        'Target Attributes',
        'Campaigns and Deals',
        'Classification and Enrollment',
        'Dining Services',
      ]}
    >
      <DataTableProvider tableKey={tableKey}>
        <UniversitiesTableComponent tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
